import React from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Highlighter from 'react-highlight-words';
import type { FilterByCallback } from 'react-bootstrap-typeahead/types/types';
import { withCopyToClipboardHoverIcon } from './HOC/withCopyToClipboardHoverIcon';

export interface SearchCacheOption {
    label: string;
    sublabel: string;
    link: string;
    image: string;
    searchResult: string;
    keywords: string[];
}

export function SearchBar({ searchCache }: { searchCache: SearchCacheOption[] }): JSX.Element {
    const inputRef = React.useRef<any>(null);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [customSearchSuggestions, setCustomSearchSuggestions] = React.useState<any[]>([]);
    useHotkeys('ctrl+/', () => inputRef.current?.focus(), [inputRef]);

    const filterBy: FilterByCallback = (option, { text }) => {
        const { searchResult, label, keywords } = option as SearchCacheOption;
        const searchText = text.toLowerCase();

        // Handle custom search syntax - show all custom suggestions when typing /
        if (searchText.startsWith('/')) {
            // For custom suggestions, always show them when typing custom syntax
            if (searchResult === 'template' || searchResult === 'production order' || searchResult === 'item') {
                // Show template suggestions when just typing /
                if (searchText === '/' && searchResult === 'template') {
                    return true;
                }
                // Show matching suggestions for partial commands
                // For /production-order, show all production-order related templates
                if (searchText === '/production-order' && label.startsWith('/production-order')) {
                    return true;
                }
                // Show matching suggestions for partial commands
                return label.toLowerCase().includes(searchText);
            }
            // For regular search cache items, don't show them when typing custom syntax
            return false;
        }

        // Regular search logic for non-custom searches
        const searchWords = splitWords(text);
        return searchWords.every((sw) => keywords.some((kw: string) => kw.includes(sw)));
    };

    const splitWords = (text: string) =>
        text
            .toLowerCase()
            .split(' ')
            .map((x) => x.trim())
            .filter((x) => x.length > 0 && x !== null);

    // Generate custom search suggestions
    const generateCustomSuggestions = (text: string) => {
        if (!text.startsWith('/')) return [];

        const suggestions: SearchCacheOption[] = [];

        if (text.startsWith('/production-order:')) {
            const number = text.replace('/production-order:', '');
            if (number.length > 0) {
                // Generate suggestions based on the number
                for (let i = 0; i < 5; i++) {
                    const suggestion = `${number}${i}`;
                    suggestions.push({
                        label: `/production-order:${suggestion}`,
                        sublabel: `Production Order #${suggestion}`,
                        searchResult: 'production order',
                        link: `/sap/live/production-app/production_orders?search=/production-order:${suggestion}`,
                        image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                        keywords: [suggestion]
                    });
                }
            } else {
                // Show specific production order search templates when just /production-order: is typed
                suggestions.push(
                    {
                        label: '/production-order:name:',
                        sublabel: 'Search by Production Order Name/Description',
                        searchResult: 'template',
                        link: `/sap/live/production-app/production_orders?search=/production-order:name:`,
                        image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                        keywords: ['production', 'order', 'name']
                    },
                    {
                        label: '/production-order:absolute-entry:',
                        sublabel: 'Search by Absolute Entry Number',
                        searchResult: 'template',
                        link: `/sap/live/production-app/production_orders?search=/production-order:absolute-entry:`,
                        image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                        keywords: ['production', 'order', 'absolute', 'entry']
                    },
                    {
                        label: '/production-order:item-no:',
                        sublabel: 'Search by Item Number',
                        searchResult: 'template',
                        link: `/sap/live/production-app/production_orders?search=/production-order:item-no:`,
                        image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                        keywords: ['production', 'order', 'item', 'no']
                    },
                    {
                        label: '/production-order:document-number:',
                        sublabel: 'Search by Document Number',
                        searchResult: 'template',
                        link: `/sap/live/production-app/production_orders?search=/production-order:document-number:`,
                        image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                        keywords: ['production', 'order', 'document', 'number']
                    }
                );
            }
        } else if (text.startsWith('/item:')) {
            const number = text.replace('/item:', '');
            if (number.length > 0 && !text.includes(':')) {
                // Only show item code suggestions if it's a simple /item:number format
                for (let i = 0; i < 5; i++) {
                    const suggestion = `${number}${i}`;
                    suggestions.push({
                        label: `/item:${suggestion}`,
                        sublabel: `Item #${suggestion}`,
                        searchResult: 'item',
                        link: `/sap/live/production-app/items?search=/item:${suggestion}`,
                        image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                        keywords: [suggestion]
                    });
                }
            } else if (text === '/item:') {
                // Show specific item search templates when just /item: is typed
                suggestions.push(
                    {
                        label: '/item:',
                        sublabel: 'Search by Item Code',
                        searchResult: 'template',
                        link: `/sap/live/production-app/items?search=/item:`,
                        image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                        keywords: ['item', 'code']
                    },
                    {
                        label: '/item:category:',
                        sublabel: 'Search by Category',
                        searchResult: 'template',
                        link: `/sap/live/production-app/items?search=/item:category:`,
                        image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                        keywords: ['category']
                    },
                    {
                        label: '/item:status:',
                        sublabel: 'Search by Status',
                        searchResult: 'template',
                        link: `/sap/live/production-app/items?search=/item:status:`,
                        image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                        keywords: ['status']
                    }
                );
            }
        } else if (text === '/') {
            // Show all available search types
            suggestions.push(
                {
                    label: '/production-order:',
                    sublabel: 'Search Production Orders (multiple options)',
                    searchResult: 'template',
                    link: `/sap/live/production-app/production_orders?search=/production-order:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['production', 'order']
                },

                {
                    label: '/item:',
                    sublabel: 'Search Items (multiple options)',
                    searchResult: 'template',
                    link: `/sap/live/production-app/items?search=/item:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['item']
                },
                {
                    label: '/create-item-materials:',
                    sublabel: 'Create Item Materials View',
                    searchResult: 'template',
                    link: `/sap/live/production-app/create_materials?search=/create-item-materials:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['create', 'materials', 'item']
                }
            );
        } else if (text === '/production-order') {
            // Show all production order search templates
            suggestions.push(
                {
                    label: '/production-order:name:',
                    sublabel: 'Search by Production Order Name/Description',
                    searchResult: 'template',
                    link: `/sap/live/production-app/production_orders?search=/production-order:name:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['production', 'order', 'name']
                },
                {
                    label: '/production-order:absolute-entry:',
                    sublabel: 'Search by Absolute Entry Number',
                    searchResult: 'template',
                    link: `/sap/live/production-app/production_orders?search=/production-order:absolute-entry:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['production', 'order', 'absolute', 'entry']
                },
                {
                    label: '/production-order:item-no:',
                    sublabel: 'Search by Item Number',
                    searchResult: 'template',
                    link: `/sap/live/production-app/production_orders?search=/production-order:item-no:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['production', 'order', 'item', 'no']
                },
                {
                    label: '/production-order:document-number:',
                    sublabel: 'Search by Document Number',
                    searchResult: 'template',
                    link: `/sap/live/production-app/production_orders?search=/production-order:document-number:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['production', 'order', 'document', 'number']
                }
            );
        } else if (text === '/item') {
            // Show all item search templates
            suggestions.push(
                {
                    label: '/item:',
                    sublabel: 'Search by Item Code',
                    searchResult: 'template',
                    link: `/sap/live/production-app/items?search=/item:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['item', 'code']
                },
                {
                    label: '/item:category:',
                    sublabel: 'Search by Category',
                    searchResult: 'template',
                    link: `/sap/live/production-app/items?search=/item:category:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['category']
                },
                {
                    label: '/item:status:',
                    sublabel: 'Search by Status',
                    searchResult: 'template',
                    link: `/sap/live/production-app/items?search=/item:status:`,
                    image: `${process.env.PUBLIC_URL}/other/info_256.png`,
                    keywords: ['status']
                }
            );
        }

        return suggestions;
    };

    return (
        <Typeahead
            ref={inputRef}
            clearButton
            align="right"
            labelKey={'label'}
            id="async-example"
            filterBy={filterBy}
            isLoading={false}
            autoFocus
            minLength={1}
            maxResults={20}
            onChange={(selected: any) => {
                if (selected.length > 0) {
                    const selectedOption = selected[0];

                    // Handle template selections - just fill in the command without colon
                    if (selectedOption.searchResult === 'template') {
                        // For templates, just fill in the command name without the colon
                        const commandName = selectedOption.label.replace(':', '');

                        // Prevent setting empty values that could cause crashes
                        if (commandName && commandName.trim() !== '') {
                            inputRef.current?.clear();
                            // Set the input value to just the command name with proper null checks
                            if (inputRef.current && inputRef.current._input) {
                                inputRef.current._input.value = commandName;
                                inputRef.current._input.focus();
                            }
                        } else {
                            // If the template would result in an empty value, just clear and focus
                            inputRef.current?.clear();
                            if (inputRef.current && inputRef.current._input) {
                                inputRef.current._input.focus();
                            }
                        }

                        // Clear any existing search parameters to prevent search from starting
                        const currentParams = new URLSearchParams(searchParams);
                        currentParams.delete('search');
                        setSearchParams(currentParams);
                        return; // Exit early to prevent further processing
                    } else {
                        // For regular selections, navigate to the link
                        inputRef.current?.clear();
                        navigate(selectedOption.link);
                    }
                } else {
                    // Clear search parameter from URL when selection is cleared
                    const currentParams = new URLSearchParams(searchParams);
                    currentParams.delete('search');
                    setSearchParams(currentParams);
                }
            }}
            onInputChange={(text: string) => {
                // Update custom suggestions based on input
                const suggestions = generateCustomSuggestions(text);
                setCustomSearchSuggestions(suggestions);

                // Clear search parameter from URL if input is empty
                if (!text || text.trim() === '') {
                    const currentParams = new URLSearchParams(searchParams);
                    currentParams.delete('search');
                    setSearchParams(currentParams);
                }
            }}
            onKeyDown={(e: any) => {
                // Handle custom search syntax on Enter
                if (e.key === 'Enter') {
                    const text = e.target.value;
                    if (text.includes('/production-order:')) {
                        e.preventDefault();
                        inputRef.current?.clear();
                        // Navigate to production orders tab with search parameter
                        navigate(`/sap/live/production-app/production_orders?search=${encodeURIComponent(text)}`);
                    } else if (text.includes('/item:')) {
                        e.preventDefault();
                        inputRef.current?.clear();
                        // Navigate to items tab with search parameter
                        navigate(`/sap/live/production-app/items?search=${encodeURIComponent(text)}`);
                    } else if (text.includes('/create-item-materials:')) {
                        e.preventDefault();
                        inputRef.current?.clear();
                        // Navigate to create materials tab with search parameter
                        navigate(`/sap/live/production-app/create_materials?search=${encodeURIComponent(text)}`);
                    }
                }
            }}
            inputProps={{ 'aria-label': 'Search', spellCheck: 'false' }}
            options={[...customSearchSuggestions, ...searchCache] as any}
            placeholder='Search... or /production-order:12345    ctrl+"/"'
            renderMenuItemChildren={(option: any, { text }) => {
                const searchWords = splitWords(text);

                const Comp = (props: any) => (
                    <Highlighter highlightClassName="highlighterSearch" searchWords={searchWords} textToHighlight={option.label} {...props} />
                );

                const WithCC = withCopyToClipboardHoverIcon(Comp);

                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            width: '100%'
                        }}
                    >
                        <img src={option.image} alt="" style={{ width: '48px', height: '48px', marginRight: '10px' }} />
                        <div>
                            <div>
                                <WithCC textToCopy={option.label} />
                            </div>
                            <small className="text-muted">
                                {typeof option.sublabel === 'string' ? (
                                    <Highlighter highlightClassName="highlighterSearch" searchWords={searchWords} textToHighlight={option.sublabel} />
                                ) : (
                                    option.sublabel
                                )}
                            </small>
                        </div>
                        <div style={{ marginLeft: 'auto' }}>
                            <small className="text-muted">{option.searchResult}</small>
                        </div>
                    </div>
                );
            }}
        />
    );
}
